import type { ReactNode } from "react";
import useTranslation from "../../client/hooks/useTranslation";
import { resolvePropFirmLogo } from "../../trading-accounts/tradovate/tradovatePropFirmLogo";
import type { LiteFirm, LiteStatus } from "../data/LiteDataProvider";
import { useLiteData } from "../data/LiteDataProvider";
import { pnlClass } from "../lib/format";

export type Tone = "gain" | "loss" | "brand" | "warn";

/** Variable CSS (HSL) de chaque ton, pour les graphiques et les styles inline. */
export const TONE_VAR: Record<Tone, string> = {
  gain: "--edgely-pnl-positive",
  loss: "--edgely-pnl-negative",
  brand: "--primary",
  warn: "--warning",
};
export const toneColor = (tone: Tone, alpha?: number) => `hsl(var(${TONE_VAR[tone]})${alpha !== undefined ? ` / ${alpha}` : ""})`;

const TONE_TEXT: Record<Tone, string> = { gain: "text-pnlPositive", loss: "text-pnlNegative", brand: "text-primary", warn: "text-warning" };
const TONE_BG: Record<Tone, string> = { gain: "bg-pnlPositive", loss: "bg-pnlNegative", brand: "bg-primary", warn: "bg-warning" };

export const cardCls = "bg-card border border-border rounded-2xl min-w-0";
export const labelCls = "block text-xs font-medium text-muted-foreground mb-1.5";

export function Card({ title, subtitle, action, children, className = "", bodyClassName = "", pad = true }: {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  pad?: boolean;
}) {
  return (
    <section className={`${cardCls} ${className}`}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-3 px-5 pt-4 pb-1">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-semibold text-foreground">{title}</h2>}
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      <div className={`${pad ? "px-5 pb-5 pt-3" : ""} ${bodyClassName}`}>{children}</div>
    </section>
  );
}

export function Pnl({ value, decimals, className = "" }: { value: number; decimals?: boolean; className?: string }) {
  const { fmt } = useLiteData();
  return <span className={`font-mono tabular-nums ${pnlClass(value)} ${className}`}>{fmt.money(value, { decimals })}</span>;
}

export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: "gain" | "loss" }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-mono tabular-nums text-xl font-semibold mt-1 truncate ${tone ? TONE_TEXT[tone] : "text-foreground"}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground/70 mt-0.5 truncate">{hint}</div>}
    </div>
  );
}

export function Bar({ value, tone = "brand", className = "" }: { value: number; tone?: Tone; className?: string }) {
  return (
    <div className={`h-1.5 rounded-full bg-muted overflow-hidden ${className}`}>
      <div className={`h-full rounded-full ${TONE_BG[tone]} transition-[width] duration-500`} style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} />
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange, size = "md" }: {
  value: T;
  options: { id: T; label: string }[];
  onChange: (v: T) => void;
  size?: "sm" | "md";
}) {
  return (
    <div className="inline-flex max-w-full overflow-x-auto p-0.5 rounded-xl bg-muted border border-border [scrollbar-width:none]" role="tablist">
      {options.map(o => (
        <button
          key={o.id}
          type="button"
          role="tab"
          aria-selected={value === o.id}
          onClick={() => onChange(o.id)}
          className={`${size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-sm"} rounded-[10px] font-medium transition-colors whitespace-nowrap ${
            value === o.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Le logo TPT livré avec l'app est quasi vide : le Lite utilise la nouvelle version.
const LOGO_OVERRIDES: Record<string, string> = { takeprofittrader: "/logos/propfirms/tpt.png" };

/** Logo d'une prop firm pour le Lite : override éventuel, sinon la résolution de l'app. */
export function liteFirmLogo(firm: { slug: string; logoUrl?: string | null; color?: string | null }): { src: string | null; color: string | null } {
  const override = LOGO_OVERRIDES[firm.slug.toLowerCase()];
  if (override) return { src: override, color: firm.color ?? null };
  const r = resolvePropFirmLogo({ slug: firm.slug, logoUrl: firm.logoUrl ?? null, color: firm.color ?? "" });
  return { src: r.src, color: r.color };
}

export function FirmMark({ firm, size = "md" }: { firm: LiteFirm | null; size?: "sm" | "md" }) {
  const dim = size === "sm" ? "w-6 h-6 rounded-md text-[10px]" : "w-9 h-9 rounded-xl text-xs";
  if (!firm) return <div className={`${dim} shrink-0 grid place-items-center font-display font-bold bg-muted text-muted-foreground`}>P</div>;
  const src = LOGO_OVERRIDES[firm.slug.toLowerCase()] ?? resolvePropFirmLogo({ slug: firm.slug, logoUrl: null, color: firm.color }).src;
  if (src) return <img src={src} alt={firm.name} className={`${dim} shrink-0 object-cover border border-border bg-white/90`} />;
  return (
    <div className={`${dim} shrink-0 grid place-items-center font-display font-bold`} style={{ background: firm.color, color: firm.textColor }}>
      {firm.name.slice(0, 2).toUpperCase()}
    </div>
  );
}

export function StatusBadge({ status }: { status: LiteStatus }) {
  const t = useTranslation();
  const tone = status === "breached" ? "bg-pnlNegative/10 text-pnlNegative"
    : status === "funded" || status === "live" ? "bg-primary/10 text-primary"
    : status === "inactive" ? "bg-muted text-muted-foreground/70"
    : "bg-muted text-muted-foreground";
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${tone}`}>{t.lite.common.statuses[status]}</span>;
}

export function Empty({ title, action, children }: { title: string; action?: ReactNode; children?: ReactNode }) {
  return (
    <div className="text-center py-12 px-6">
      <div className="text-sm font-medium text-foreground">{title}</div>
      {children && <div className="text-sm text-muted-foreground mt-1">{children}</div>}
      {action && <div className="mt-5 flex justify-center gap-2">{action}</div>}
    </div>
  );
}

export { TONE_TEXT, TONE_BG };
